import _regeneratorRuntime from '@babel/runtime/helpers/esm/regeneratorRuntime';
import _objectSpread from '@babel/runtime/helpers/esm/objectSpread2';
import _asyncToGenerator from '@babel/runtime/helpers/esm/asyncToGenerator';
import _createForOfIteratorHelper from '@babel/runtime/helpers/esm/createForOfIteratorHelper';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import { DrmQuality } from '../utils.js';
import '@dtvgo/player-common-utils';

/**
 * Retrieves the playback sources based on the provided playback info and authentication headers.
 *
 * @param {Object} options - The options object.
 * @param {Object} options.playbackInfo - The playback information.
 * @param {string} options.playbackInfo.drmId - The DRM ID.
 * @param {DrmOptions} options.playbackInfo.drms - The DRM configurations.
 * @param {ManifestObject} options.playbackInfo.manifest - The playback manifest.
 * @param {import('../utils').GetHeadersCallback} options.getAuthHeaders - A function to retrieve
 * the authentication headers.
 * @return {Source[]} The array of playback sources.
 */
function getPlaybackSources(_ref) {
  var _ref$playbackInfo = _ref.playbackInfo,
    drmId = _ref$playbackInfo.drmId,
    drms = _ref$playbackInfo.drms,
    manifest = _ref$playbackInfo.manifest,
    getAuthHeaders = _ref.getAuthHeaders;
  var keySystemsConfigPerQuality = getKeySystemsConfigPerQuality({
    drms: drms,
    drmId: drmId,
    getAuthHeaders: getAuthHeaders
  });
  return getSourceObjectArray(manifest, function (drmQuality) {
    return keySystemsConfigPerQuality[drmQuality];
  });
}
var KeySystemConfigGetter = {
  playready: getPlayreadyConfig,
  widevine: getWidevineConfig,
  fairplay: getFairplayConfig
};

/**
 * Generates the key systems configuration per quality based on the provided DRM configurations.
 *
 * @param {Object} options - The options object.
 * @param {DrmOptions} options.drms - The DRM configurations.
 * @param {string} [options.drmId] - The DRM ID.
 * @param {import('../utils').GetHeadersCallback} options.getAuthHeaders - The function to
 * get the authentication headers.
 * @return {{ [quality: DrmQuality]: KeySystemsConfig }} The key systems configuration per quality.
 */
function getKeySystemsConfigPerQuality(_ref2) {
  var drms = _ref2.drms,
    drmId = _ref2.drmId,
    getAuthHeaders = _ref2.getAuthHeaders;
  if (!drms) return {};
  var keySystemsConfigPerQuality = {};
  for (var _i = 0, _Object$entries = Object.entries(drms); _i < _Object$entries.length; _i++) {
    var _Object$entries$_i = _slicedToArray(_Object$entries[_i], 2),
      keySystemName = _Object$entries$_i[0],
      drmConfigs = _Object$entries$_i[1];
    var getKeySystemConfig = KeySystemConfigGetter[keySystemName];
    if (!getKeySystemConfig || !drmConfigs) continue;
    var _iterator = _createForOfIteratorHelper(drmConfigs),
      _step;
    try {
      for (_iterator.s(); !(_step = _iterator.n()).done;) {
        var drmConfig = _step.value;
        var qualityToUse = drmConfig.quality || DrmQuality.DEFAULT;
        if (!keySystemsConfigPerQuality[qualityToUse]) keySystemsConfigPerQuality[qualityToUse] = {};
        keySystemsConfigPerQuality[qualityToUse][keySystemName] = getKeySystemConfig({
          config: drmConfig,
          drmId: drmId,
          getAuthHeaders: getAuthHeaders
        });
      }
    } catch (err) {
      _iterator.e(err);
    } finally {
      _iterator.f();
    }
  }
  return keySystemsConfigPerQuality;
}

/**
 * Generates the Playready configuration object.
 *
 * @param {object} options - The options object.
 * @param {DRMConfig} options.config - The configuration object.
 * @param {string} [options.drmId] - The DRM ID.
 * @param {import('../utils').GetHeadersCallback} options.getAuthHeaders - The function to get
 * authentication headers.
 * @return {KeySystemConfig} The Playready configuration object.
 */
function getPlayreadyConfig(_ref3) {
  var config = _ref3.config,
    drmId = _ref3.drmId,
    getAuthHeaders = _ref3.getAuthHeaders;
  return {
    defaultLicenseUrl: getLicenseUrl(config.licenseUrl, drmId),
    getLicenseHeaders: function () {
      var _getLicenseHeaders = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee() {
        var _args = arguments;
        return _regeneratorRuntime().wrap(function _callee$(_context) {
          while (1) switch (_context.prev = _context.next) {
            case 0:
              _context.t0 = _objectSpread;
              _context.t1 = _objectSpread;
              _context.t2 = {};
              _context.next = 5;
              return getAuthHeaders.apply(void 0, _args);
            case 5:
              _context.t3 = _context.sent;
              _context.t4 = (0, _context.t1)(_context.t2, _context.t3);
              _context.t5 = {};
              _context.t6 = {
                'Content-Type': 'text/xml; charset=utf-8',
                SOAPAction: 'http://schemas.microsoft.com/DRM/2007/03/protocols/AcquireLicense'
              };
              return _context.abrupt("return", (0, _context.t0)(_context.t4, _context.t5, _context.t6));
            case 10:
            case "end":
              return _context.stop();
          }
        }, _callee);
      }));
      function getLicenseHeaders() {
        return _getLicenseHeaders.apply(this, arguments);
      }
      return getLicenseHeaders;
    }()
  };
}

/**
 * Generates the Widevine configuration object.
 *
 * @param {object} options - The options object.
 * @param {DRMConfig} options.config - The configuration object.
 * @param {string} [options.drmId] - The DRM ID.
 * @param {import('../utils').GetHeadersCallback} options.getAuthHeaders - The function to get
 * authentication headers.
 * @return {KeySystemConfig} The Widevine configuration object.
 */
function getWidevineConfig(_ref4) {
  var config = _ref4.config,
    drmId = _ref4.drmId,
    getAuthHeaders = _ref4.getAuthHeaders;
  return {
    defaultLicenseUrl: getLicenseUrl(config.licenseUrl, drmId),
    getLicenseHeaders: getAuthHeaders
  };
}

/**
 * Generates the Fairplay configuration object.
 *
 * @param {object} options - The options object.
 * @param {DRMConfig} options.config - The configuration object.
 * @param {string} [options.drmId] - The DRM ID.
 * @param {import('../utils').GetHeadersCallback} options.getAuthHeaders - The function to get
 * authentication headers.
 * @return {KeySystemConfig} The Fairplay configuration object.
 */
function getFairplayConfig(_ref5) {
  var config = _ref5.config,
    drmId = _ref5.drmId,
    getAuthHeaders = _ref5.getAuthHeaders;
  return {
    defaultLicenseUrl: getLicenseUrl(config.licenseUrl, drmId),
    certificateUrl: config.certificateUrl,
    getLicenseHeaders: getAuthHeaders
  };
}

/**
 * Generates a URL with the specified DRM ID appended to the query string.
 *
 * @param {string} licenseUrl - The original URL without the DRM ID.
 * @param {string} [drmId] - The DRM ID to append to the URL.
 * @return {string} The modified URL with the DRM ID appended.
 */
function getLicenseUrl(licenseUrl, drmId) {
  if (!drmId) return licenseUrl;
  var url = new URL(licenseUrl);
  url.searchParams.set('contentId', drmId);
  return url.toString();
}

/**
 * Returns an array of sources to be used in the player as alternatives
 * @param {ManifestObject} manifestObject - The urls object
 * @param {(drmQuality: DrmQuality) => KeySystemsConfig} getKeySystemsConfig - A function to get the
 *  keySystemsConfig based on the source drmQuality
 * @returns {Source[]} An array with source objects
 */
function getSourceObjectArray(manifestObject, getKeySystemsConfig) {
  if (!manifestObject) return undefined;
  var _manifestObject$dash = manifestObject.dash,
    _manifestObject$dash2 = _manifestObject$dash === void 0 ? {} : _manifestObject$dash,
    primaryDashUrl = _manifestObject$dash2.primary,
    backupDashUrl = _manifestObject$dash2.backup,
    lowLatencyDashUrl = _manifestObject$dash2.lowLatency,
    _manifestObject$hls = manifestObject.hls,
    _manifestObject$hls2 = _manifestObject$hls === void 0 ? {} : _manifestObject$hls,
    primaryHlsUrl = _manifestObject$hls2.primary,
    backupHlsUrl = _manifestObject$hls2.backup,
    lowLatencyHlsUrl = _manifestObject$hls2.lowLatency;
  var urlsArray = [{
    dash: lowLatencyDashUrl,
    hls: lowLatencyHlsUrl,
    drmQuality: DrmQuality.LOW_LATENCY,
    isLowLatency: true,
    isPrimary: false
  }, {
    dash: primaryDashUrl,
    hls: primaryHlsUrl,
    drmQuality: DrmQuality.DEFAULT,
    isLowLatency: false,
    isPrimary: true
  }, {
    dash: backupDashUrl,
    hls: backupHlsUrl,
    drmQuality: DrmQuality.DEFAULT,
    isLowLatency: false,
    isPrimary: false
  }];
  return urlsArray.filter(function (source) {
    return source.dash || source.hls;
  }).map(function (source) {
    return _objectSpread(_objectSpread({}, source), {}, {
      keySystemsConfig: getKeySystemsConfig(source.drmQuality)
    });
  });
}

/**
 * @typedef {object} DRMConfig
 * @property {string} licenseUrl - The license URL.
 * @property {string} [certificateUrl] - The certificate URL.
 * @property {DrmQuality} [quality] - The quality of the DRM Config.
 */

/**
 * @typedef {object} DrmOptions
 * @property {DRMConfig[]} widevine - The Google's Widevine DRMconfigs.
 * @property {DRMConfig[]} playready - The Microsoft's Playready DRMconfigs.
 * @property {DRMConfig[]} fairplay - The Apple's Fairplay DRMconfigs.
 */

/**
 * @typedef {object} ManifestFormatOptions
 * @property {string} primary - The primary manifest URL.
 * @property {string} [backup] - The backup manifest URL.
 * @property {string} [lowLatency] - The low latency manifest URL.
 */

/**
 * @typedef {object} ManifestObject
 * @property {ManifestFormatOptions} dash - The DASH manifest object.
 * @property {ManifestFormatOptions} hls - The HLS manifest object.
 */

/**
 * @typedef {object} KeySystemConfig
 * @property {string} defaultLicenseUrl - The default license URL.
 * @property {import('../utils').GetHeadersCallback} getLicenseHeaders - The function to get the
 * headers for license request.
 * @property {string} [certificateUrl] - The name of the header that contains the
 */

/**
 * @typedef {object} KeySystemsConfig
 * @property {KeySystemConfig} playready - The Playready config.
 * @property {KeySystemConfig} widevine - The Widevine config.
 * @property {KeySystemConfig} fairplay - The Fairplay config.
 */

/**
 * @typedef {object} Source
 * @property {string} dash - The DASH URL.
 * @property {string} hls - The HLS URL.
 * @property {DrmQuality} drmQuality - The DRM quality.
 * @property {boolean} isLowLatency - Whether the source is low latency.
 * @property {boolean} isPrimary - Whether the source is primary.
 * @property {KeySystemsConfig} keySystemsConfig - The keySystemsConfig for the source.
 */

export { getPlaybackSources };
//# sourceMappingURL=getPlaybackSources.js.map
